import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { ItemTypes } from '../../constants/constants'
import { sketchCells } from '../../shared/compositions/compositionGeometry'

/**
 * Croquis d'une disposition de conteneur : une case par colonne, subdivisée
 * quand la colonne est une pile de lignes, avec la nature du contenu quand la
 * disposition en prévoit.
 *
 * Composant unique et partagé volontairement : la même disposition apparaît
 * dans le panneau de gauche, dans le menu « + » entre deux conteneurs et dans
 * les préréglages du panneau de droite. Trois croquis différents pour la même
 * chose et ce sont trois fonctionnalités aux yeux de l'utilisateur.
 *
 * Deux formes de descripteur acceptées :
 *  - une disposition du catalogue (colonnes récursives, cf.
 *    constants/containerTemplates.js) ;
 *  - la forme plate `[{ size, rows: <nombre> }]` des préréglages du panneau de
 *    droite, où `rows` est un compte de lignes vides.
 */

// Le croquis n'est posé que sur fond clair (panneaux gauche et droit, page
// vide).
const LINE_COLOR = '#7bb7ff'
const GLYPH_COLOR = 'rgba(11, 34, 57, 0.55)'

// Nom d'icône par type de box. La macro FontAwesome exige des littéraux : les
// icônes sont donc construites ici une fois, pas résolues depuis une variable.
// Titres : le « T » des boutons de titre du panneau de gauche.
const GLYPHS = {
  [ItemTypes.IMG]: icon({ name: 'image', style: 'light' }),
  [ItemTypes.IMG_VARIABLE]: icon({ name: 'image', style: 'light' }),
  [ItemTypes.TEXT]: icon({ name: 'align-left', style: 'light' }),
  [ItemTypes.TEXT_VARIABLE]: icon({ name: 'align-left', style: 'light' }),
  [ItemTypes.TABLE]: icon({ name: 'table', style: 'light' }),
  [ItemTypes.SHAPE]: icon({ name: 'star', style: 'light' }),
  [ItemTypes.LINE]: icon({ name: 'dash', style: 'light' }),
  [ItemTypes.TITLE]: icon({ name: 't', style: 'light' }),
  [ItemTypes.TITLE_2]: icon({ name: 't', style: 'light' }),
  [ItemTypes.TITLE_3]: icon({ name: 't', style: 'light' }),
  [ItemTypes.TITLE_4]: icon({ name: 't', style: 'light' }),
}

const GLYPH_SIZE = 10

// Le noeud reçu est DÉJÀ normalisé par `sketchCells` : on ne renormalise pas
// en descendant, sinon les largeurs des sous-colonnes seraient perdues.
const Cell = ({ node }) => {
  if (node.rows?.length) {
    return (
      <div style={{ flex: node.weight, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {node.rows.map((row, rowIdx) => (
          <div key={rowIdx} style={{ flex: 1, display: 'flex', gap: 2 }}>
            {row.map((subNode, subIdx) => (
              <Cell key={subIdx} node={subNode} />
            ))}
          </div>
        ))}
      </div>
    )
  }

  const glyph = GLYPHS[node.box]
  return (
    <div
      style={{
        flex: node.weight,
        // `minWidth: 0` : sans lui, l'icône impose sa largeur intrinsèque et
        // une colonne d'un quart finit aussi large qu'une demie.
        minWidth: 0,
        borderRadius: 2,
        border: `1.5px dashed ${LINE_COLOR}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {glyph && (
        <FontAwesomeIcon icon={glyph} style={{ fontSize: GLYPH_SIZE, color: GLYPH_COLOR }} />
      )}
    </div>
  )
}

const ContainerTemplatePreview = ({ columns = [], height = 30 }) => {
  return (
    // Largeur fluide : c'est la tuile parente qui décide, sinon il reste du
    // blanc à droite dans les grilles de préréglages.
    <div style={{ display: 'flex', gap: 2, width: '100%', height }}>
      {sketchCells(columns).map((node, idx) => (
        <Cell key={idx} node={node} />
      ))}
    </div>
  )
}

export default ContainerTemplatePreview
